import { useEffect, useRef } from "react";

declare global {
  interface Window {
    YT?: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

// YouTube IFrame API sayfaya bir kez yüklenir; aynı anda açılan oynatıcılar aynı sözü bekler.
let apiPromise: Promise<any> | null = null;
const loadYouTubeApi = (): Promise<any> => {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve) => {
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previous?.();
      resolve(window.YT);
    };
    const script = document.createElement("script");
    script.src = "https://www.youtube.com/iframe_api";
    script.async = true;
    document.head.appendChild(script);
  });
  return apiPromise;
};

// Normal oynatmada iki okuma arası ilerleme (2× hız dahil); bundan büyük sıçrama ileri sarmadır, izlenmiş sayılmaz.
const MAX_STEP_SECONDS = 2.5;
const PLAYING = 1;

interface TrackedYouTubeProps {
  videoId: string;
  title?: string;
  /** İzlenen TEKİL saniye (aynı bölümü tekrar izlemek iki kez sayılmaz, ileri sarılan kısım hiç sayılmaz) ve video süresi.
   *  final: duraklatıldı, bitti ya da pencere kapandı — hemen bildirilmeli. */
  onWatchProgress?: (watchedSeconds: number, durationSeconds: number, final?: boolean) => void;
}

/**
 * Konu anlatımı videosu (YouTube) — izlemeyi ÖLÇER. Eskiden video yalnız açılınca kaydediliyor, izlendiği hiç
 * bilinmiyordu; föyler %100 olamıyordu (saha, 18 Eki). Oynarken saniyede bir konum okunur.
 */
export function TrackedYouTube({ videoId, title, onWatchProgress }: TrackedYouTubeProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const callbackRef = useRef(onWatchProgress);
  callbackRef.current = onWatchProgress;

  useEffect(() => {
    let player: any = null;
    let timer: number | undefined;
    let disposed = false;
    const seen = new Set<number>();
    let last: number | null = null;
    let duration = 0;

    const report = (final = false) => {
      if (duration > 0) callbackRef.current?.(seen.size, duration, final);
    };
    const sample = () => {
      if (!player?.getCurrentTime) return;
      const now = Number(player.getCurrentTime()) || 0;
      duration = Number(player.getDuration?.()) || duration;
      if (last !== null && now > last && now - last <= MAX_STEP_SECONDS) {
        for (let s = Math.floor(last); s < Math.floor(now); s += 1) seen.add(s);
      }
      last = now;
      report();
    };
    const stop = () => {
      if (timer) window.clearInterval(timer);
      timer = undefined;
    };

    loadYouTubeApi().then((YT) => {
      if (disposed || !iframeRef.current || !YT?.Player) return;
      player = new YT.Player(iframeRef.current, {
        events: {
          onStateChange: (event: any) => {
            if (event.data === PLAYING) {
              last = Number(player.getCurrentTime?.()) || 0;
              if (!timer) timer = window.setInterval(sample, 1000);
            } else {
              sample();
              stop();
              last = null;
              report(true);
            }
          },
        },
      });
    });

    // player.destroy() çağrılmaz: iframe'i React yönetiyor, YouTube'un DOM'dan sökmesi React'in ayrılmasını bozar.
    // Video değişince bileşen anahtarla (key) yeniden kurulur.
    return () => {
      disposed = true;
      stop();
      report(true);
    };
  }, [videoId]);

  const origin = typeof window !== "undefined" ? encodeURIComponent(window.location.origin) : "";
  return (
    <iframe
      ref={iframeRef}
      src={`https://www.youtube-nocookie.com/embed/${videoId}?enablejsapi=1&origin=${origin}`}
      className="absolute top-0 left-0 w-full h-full"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      title={title || `youtube-video-${videoId}`}
    />
  );
}
